import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { BillingHealthTab } from '../components/admin/BillingHealthTab';
import { RichResultsTab } from '../components/admin/RichResultsTab';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Loader2,
  LogOut,
  Building,
  Users,
  BarChart3,
  Plus,
  Edit,
  Eye,
  CheckCircle,
  Settings,
  Palette,
  FileText,
  Mail,
  CreditCard,
  Calendar,
  Code,
} from 'lucide-react';
import { formatDate } from '../lib/utils';
import HeroBannerEditor from '../components/HeroBannerEditor';

const CompanyManagementModal = ({ company, onClose, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const navigate = useNavigate();

  const handleMasquerade = async () => {
    try {
      setLoading(true);
      console.log('Attempting masquerade for company:', company.id);
      const response = await api.post(`/masquerade/${company.id}`);
      console.log('Masquerade response:', response.data);
      
      // Store masquerade data in sessionStorage with a unique key
      const masqueradeId = `masquerade_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem(masqueradeId, JSON.stringify({
        token: response.data.access_token,
        user: response.data.user
      }));
      
      // Open new window with masquerade ID
      window.open(`/settings?mid=${masqueradeId}`, '_blank');
      
    } catch (error) {
      console.error('Masquerade error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      alert(`Failed to masquerade: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm(`Are you sure you want to deactivate ${company.name}? Users will see a message to call customer service.`)) {
      return;
    }

    try {
      setLoading(true);
      console.log('Attempting deactivate for company:', company.id);
      const response = await api.patch(`/companies/${company.id}/deactivate`);
      console.log('Deactivate response:', response.data);
      alert('Company deactivated successfully');
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Deactivate error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      alert(`Failed to deactivate: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    try {
      setLoading(true);
      await api.patch(`/companies/${company.id}/activate`);
      alert('Company activated successfully');
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Activate error:', error);
      alert('Failed to activate company');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/companies/${company.id}`);
      alert('Company deleted permanently');
      onRefresh();
      onClose();
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete company');
    } finally {
      setLoading(false);
    }
  };

  const isActive = company.status !== 'inactive';

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <Card 
        className="w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader>
          <CardTitle>Manage {company.name}</CardTitle>
          <CardDescription>
            Super Admin actions for {company.signature_url}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Masquerade */}
          <div className="border rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Settings className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h4 className="font-medium mb-1">Edit Signature (Masquerade)</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Login as company admin to help edit their signature settings
                </p>
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleMasquerade();
                  }}
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? 'Loading...' : 'Masquerade as Admin'}
                </Button>
              </div>
            </div>
          </div>

          {/* Activate/Deactivate */}
          <div className="border rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                isActive ? 'bg-yellow-100' : 'bg-green-100'
              }`}>
                {isActive ? (
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <h4 className="font-medium mb-1">
                  {isActive ? 'Deactivate Signature' : 'Activate Signature'}
                </h4>
                <p className="text-sm text-gray-600 mb-3">
                  {isActive 
                    ? 'Users will see: "Call customer service to reactivate: 888-910-1123"'
                    : 'Reactivate this company\'s signature tool'}
                </p>
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isActive) {
                      handleDeactivate();
                    } else {
                      handleActivate();
                    }
                  }}
                  disabled={loading}
                  variant={isActive ? 'destructive' : 'default'}
                  className="w-full"
                >
                  {loading ? 'Loading...' : (isActive ? 'Deactivate' : 'Activate')}
                </Button>
              </div>
            </div>
          </div>

          {/* Delete */}
          <div className="border border-red-200 rounded-lg p-4 bg-red-50">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="font-medium mb-1 text-red-900">Delete Permanently</h4>
                <p className="text-sm text-red-700 mb-3">
                  {confirmDelete 
                    ? '⚠️ This action cannot be undone! All data will be lost.'
                    : 'Remove company and all associated data permanently'}
                </p>
                <Button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDelete();
                  }}
                  disabled={loading}
                  variant="destructive"
                  className="w-full"
                >
                  {loading ? 'Deleting...' : (confirmDelete ? 'Confirm Delete' : 'Delete Company')}
                </Button>
                {confirmDelete && (
                  <Button
                    type="button"
                    onClick={() => setConfirmDelete(false)}
                    variant="outline"
                    className="w-full mt-2"
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Close Button */}
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            className="w-full"
          >
            Close
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

const AdminPortal = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [stats, setStats] = useState({});
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [managingCompany, setManagingCompany] = useState(null);

  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetchData();
    } else if (user?.role === 'admin') {
      fetchCompanyData(user.company_id);
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [companiesRes, statsRes] = await Promise.all([
        api.get('/companies'),
        api.get('/stats/admin'),
      ]);

      setCompanies(companiesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyData = async (companyId) => {
    try {
      setLoading(true);
      const [companyRes, statsRes] = await Promise.all([
        api.get(`/companies/${companyId}`),
        api.get(`/stats/company/${companyId}`),
      ]);

      setSelectedCompany(companyRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching company data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleViewSignature = (company) => {
    window.open(`/clients/${company.signature_url}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-pink-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black shadow-sm border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img src="https://cdn.slicksignature.com/landing/slicksignature-logo.png" alt="SlickSignature" className="h-12" />
              <Badge
                variant="secondary"
                className="bg-pink-600 text-white"
              >
                {user?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
              </Badge>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-400">
                {user?.first_name} {user?.last_name}
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-white border-white hover:bg-gray-800"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {user?.role === 'super_admin' ? 'Total Clients' : 'Your Company'}
              </CardTitle>
              <Building className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {user?.role === 'super_admin'
                  ? stats.total_companies || 0
                  : selectedCompany?.name || '-'}
              </div>
              <p className="text-xs text-muted-foreground">
                {user?.role === 'super_admin'
                  ? 'Subscribed companies'
                  : 'Company name'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {user?.role === 'super_admin' ? 'Active Companies' : 'Admins'}
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {user?.role === 'super_admin'
                  ? stats.active_companies || 0
                  : stats.total_admins || 1}
              </div>
              <p className="text-xs text-muted-foreground">
                {user?.role === 'super_admin' ? 'Currently active' : 'Company admins'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Signatures Generated
              </CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.total_generations || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Total employee signatures
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Recent Activity
              </CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.recent_generations || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                Last 10 generations
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue={user?.role === 'super_admin' ? 'companies' : 'template'} className="space-y-6">
          <TabsList>
            {user?.role === 'super_admin' && (
              <>
                <TabsTrigger value="companies">Client Companies</TabsTrigger>
                <TabsTrigger value="register">Register New Client</TabsTrigger>
                <TabsTrigger value="default-signature">Default Signature</TabsTrigger>
                <TabsTrigger value="billing-health" data-testid="billing-health-tab-trigger">Billing Health</TabsTrigger>
                <TabsTrigger value="rich-results" data-testid="rich-results-tab-trigger">Rich Results</TabsTrigger>
                <TabsTrigger value="blog">Blog</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
              </>
            )}
            <TabsTrigger value="template">Signature Template</TabsTrigger>
            <TabsTrigger value="generations">Generated Signatures</TabsTrigger>
          </TabsList>

          {/* Companies Tab (Super Admin Only) */}
          {user?.role === 'super_admin' && (
            <>
              <TabsContent value="companies">
                <CompaniesTab
                  companies={companies}
                  onRefresh={fetchData}
                  onSelectCompany={(company) => {
                    setSelectedCompany(company);
                    fetchCompanyData(company.id);
                  }}
                  onViewSignature={handleViewSignature}
                  setManagingCompany={setManagingCompany}
                  setManageModalOpen={setManageModalOpen}
                />
              </TabsContent>

              <TabsContent value="billing-health">
                <BillingHealthTab />
              </TabsContent>

              <TabsContent value="rich-results">
                <RichResultsTab />
              </TabsContent>

              <TabsContent value="register">
                <RegisterCompanyTab onSuccess={fetchData} />
              </TabsContent>

              <TabsContent value="default-signature">
                <DefaultSignatureTab />
              </TabsContent>

              <TabsContent value="blog">
                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Blog Management</CardTitle>
                      <CardDescription>Create and manage blog posts for SEO and content marketing</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button
                        onClick={() => navigate('/admin/blog')}
                        className="w-full bg-[#EB2F5B] hover:bg-[#d02850]"
                        size="lg"
                      >
                        <FileText className="h-5 w-5 mr-2" />
                        Manage Blog Posts
                      </Button>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>SEO Tools</CardTitle>
                      <CardDescription>XML sitemap for search engine indexing</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-semibold text-sm mb-1">XML Sitemap</h4>
                            <p className="text-sm text-gray-600">
                              Submit this sitemap to Google Search Console and Bing Webmaster Tools
                            </p>
                          </div>
                          <Button
                            onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`, '_blank')}
                            variant="outline"
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            View Sitemap
                          </Button>
                        </div>
                        <div className="bg-gray-100 rounded p-3">
                          <code className="text-xs text-gray-700">
                            {window.location.hostname === 'localhost' || window.location.hostname.includes('preview') 
                              ? `${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`
                              : 'https://slicksignature.com/api/sitemap.xml'
                            }
                          </code>
                        </div>
                        <Alert>
                          <AlertDescription className="text-sm">
                            <strong>Tip:</strong> Copy the URL above and submit it to{' '}
                            <a 
                              href="https://search.google.com/search-console" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[#EB2F5B] underline"
                            >
                              Google Search Console
                            </a>
                            {' '}to get your blog posts indexed by Google.
                          </AlertDescription>
                        </Alert>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="settings">
                <Card>
                  <CardHeader>
                    <CardTitle>System Settings</CardTitle>
                    <CardDescription>Configure email, payments, and trial settings</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid gap-4">
                      <Button
                        onClick={() => navigate('/admin/email-settings')}
                        variant="outline"
                        className="justify-start h-auto py-4"
                      >
                        <div className="flex items-start space-x-4">
                          <Mail className="h-6 w-6 text-primary mt-1" />
                          <div className="text-left">
                            <div className="font-semibold">Email Configuration</div>
                            <div className="text-sm text-gray-500">Configure Gmail SMTP with OAuth2 for sending emails</div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        onClick={() => navigate('/admin/stripe-settings')}
                        variant="outline"
                        className="justify-start h-auto py-4"
                      >
                        <div className="flex items-start space-x-4">
                          <CreditCard className="h-6 w-6 text-primary mt-1" />
                          <div className="text-left">
                            <div className="font-semibold">Stripe Configuration</div>
                            <div className="text-sm text-gray-500">Connect your Stripe account to process payments</div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        onClick={() => navigate('/admin/trial-settings')}
                        variant="outline"
                        className="justify-start h-auto py-4"
                      >
                        <div className="flex items-start space-x-4">
                          <Calendar className="h-6 w-6 text-primary mt-1" />
                          <div className="text-left">
                            <div className="font-semibold">Free Trial Settings</div>
                            <div className="text-sm text-gray-500">Manage trial duration and enable/disable trials</div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        onClick={() => navigate('/admin/email-templates')}
                        variant="outline"
                        className="justify-start h-auto py-4"
                      >
                        <div className="flex items-start space-x-4">
                          <FileText className="h-6 w-6 text-primary mt-1" />
                          <div className="text-left">
                            <div className="font-semibold">Email Templates</div>
                            <div className="text-sm text-gray-500">Customize email content and messages sent to users</div>
                          </div>
                        </div>
                      </Button>

                      {/* Tracking Codes Button */}
                      <Button
                        variant="outline"
                        className="w-full h-auto py-4 justify-start text-left border-2 hover:border-primary hover:bg-gray-50"
                        onClick={() => navigate('/admin/tracking-codes')}
                      >
                        <div className="flex items-start space-x-4">
                          <Code className="h-6 w-6 text-primary mt-1" />
                          <div className="text-left">
                            <div className="font-semibold">Tracking Codes</div>
                            <div className="text-sm text-gray-500">Manage analytics and conversion tracking codes</div>
                          </div>
                        </div>
                      </Button>

                      {/* XML Sitemap Button */}
                      <Button
                        variant="outline"
                        className="w-full h-auto py-4 justify-start text-left border-2 hover:border-primary hover:bg-gray-50"
                        onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`, '_blank')}
                      >
                        <div className="flex items-start space-x-4">
                          <FileText className="h-6 w-6 text-primary mt-1" />
                          <div className="text-left">
                            <div className="font-semibold">XML Sitemap</div>
                            <div className="text-sm text-gray-500">View sitemap for Google Search Console submission</div>
                          </div>
                        </div>
                      </Button>

                      {/* Hero Banner Editor */}
                      <HeroBannerEditor />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </>
          )}

          {/* Signature Template Tab */}
          <TabsContent value="template">
            <SignatureTemplateTab
              companyId={user?.role === 'super_admin' ? selectedCompany?.id : user?.company_id}
              selectedCompany={selectedCompany}
              onRefresh={() => {
                if (user?.role === 'super_admin' && selectedCompany) {
                  fetchCompanyData(selectedCompany.id);
                } else if (user?.company_id) {
                  fetchCompanyData(user.company_id);
                }
              }}
            />
          </TabsContent>

          {/* Generations Tab */}
          <TabsContent value="generations">
            <GenerationsTab
              companyId={user?.role === 'super_admin' ? selectedCompany?.id : user?.company_id}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Company Management Modal */}
      {manageModalOpen && managingCompany && (
        <CompanyManagementModal
          company={managingCompany}
          onClose={() => {
            setManageModalOpen(false);
            setManagingCompany(null);
          }}
          onRefresh={fetchData}
        />
      )}
    </div>
  );
};

// Companies Tab Component
const CompaniesTab = ({ companies, onRefresh, onSelectCompany, onViewSignature, setManagingCompany, setManageModalOpen }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Client Companies</CardTitle>
        <CardDescription>
          Manage all client companies subscribed to SlickSignature
        </CardDescription>
      </CardHeader>
      <CardContent>
        {companies.length === 0 ? (
          <div className="text-center py-8">
            <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No companies registered
            </h3>
            <p className="text-gray-500">
              Get started by registering your first client company.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {companies.map((company) => (
              <div
                key={company.id}
                className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: company.brand_color }}
                      />
                      <h3 className="text-lg font-medium text-gray-900">
                        {company.name}
                      </h3>
                      <Badge variant="outline">{company.signature_url}</Badge>
                      <Badge
                        variant={
                          company.status === 'inactive' ? 'destructive' : 'default'
                        }
                        className={
                          company.status === 'inactive' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }
                      >
                        {company.status === 'inactive' ? 'Inactive' : 'Active'}
                      </Badge>
                    </div>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Website:</span>{' '}
                        {company.website || 'Not set'}
                      </div>
                      <div>
                        <span className="font-medium">Admin:</span>{' '}
                        {company.admin_email}
                      </div>
                      <div>
                        <span className="font-medium">URL:</span>{' '}
                        /signature/{company.signature_url}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/signature/${company.signature_url}`, '_blank')}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Tool
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setManagingCompany(company);
                        setManageModalOpen(true);
                      }}
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Manage
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Register Company Tab Component (same as before, keeping it for completeness)
const RegisterCompanyTab = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    signature_url: '',
    admin_first_name: '',
    admin_last_name: '',
    admin_email: '',
    brand_color: '#EB2F5B',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleInputChange = (field, value) => {
    if (field === 'name' && value) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setFormData((prev) => ({ 
        ...prev, 
        [field]: value,
        signature_url: slug 
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.post('/companies', formData);
      setSuccess(true);
      setFormData({
        name: '',
        website: '',
        signature_url: '',
        admin_first_name: '',
        admin_last_name: '',
        admin_email: '',
        brand_color: '#EB2F5B',
      });
      if (onSuccess) {
        try {
          await onSuccess();
        } catch (err) {
          console.error('Error in onSuccess callback:', err);
        }
      }
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to register company');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Building className="h-5 w-5 mr-2" />
          Register New Client Company
        </CardTitle>
        <CardDescription>
          Create a signature tool for a new client. They'll get a unique URL where their employees can generate signatures.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Company Information</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company-name">Company Name *</Label>
                <Input
                  id="company-name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Acme Corporation"
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="www.acme.com"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="signature-url">Signature Tool URL *</Label>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500">
                  slicksignature.com/signature/
                </span>
                <Input
                  id="signature-url"
                  value={formData.signature_url}
                  onChange={(e) =>
                    handleInputChange('signature_url', e.target.value)
                  }
                  placeholder="acme-corporation"
                  required
                  disabled={loading}
                  className="flex-1"
                />
              </div>
              <p className="text-xs text-gray-500">
                Employees will visit this URL to generate their signatures
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="brand-color">Brand Color</Label>
              <div className="flex items-center space-x-2">
                <Input
                  id="brand-color"
                  type="color"
                  value={formData.brand_color}
                  onChange={(e) =>
                    handleInputChange('brand_color', e.target.value)
                  }
                  className="w-12 h-10 p-1 cursor-pointer"
                  disabled={loading}
                />
                <Input
                  value={formData.brand_color}
                  onChange={(e) =>
                    handleInputChange('brand_color', e.target.value)
                  }
                  placeholder="#EB2F5B"
                  className="flex-1"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-medium">Admin Contact Information</h3>
            <p className="text-sm text-gray-600">
              This person will manage the company's signature template
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="admin-first-name">Admin First Name *</Label>
                <Input
                  id="admin-first-name"
                  value={formData.admin_first_name}
                  onChange={(e) =>
                    handleInputChange('admin_first_name', e.target.value)
                  }
                  placeholder="John"
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin-last-name">Admin Last Name *</Label>
                <Input
                  id="admin-last-name"
                  value={formData.admin_last_name}
                  onChange={(e) =>
                    handleInputChange('admin_last_name', e.target.value)
                  }
                  placeholder="Doe"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-email">Admin Email *</Label>
              <Input
                id="admin-email"
                type="email"
                value={formData.admin_email}
                onChange={(e) =>
                  handleInputChange('admin_email', e.target.value)
                }
                placeholder="admin@acme.com"
                required
                disabled={loading}
              />
              <p className="text-xs text-gray-500">
                We'll create an admin account with this email. Default password: <strong>SlickSig123!</strong>
              </p>
            </div>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Company registered successfully! Admin can now log in and customize their signature template.
              </AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={loading}
            style={{ backgroundColor: '#EB2F5B' }}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Registering Company...
              </>
            ) : (
              <>
                <Building className="mr-2 h-4 w-4" />
                Register Company
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Signature Template Tab Component
const SignatureTemplateTab = ({ companyId, selectedCompany, onRefresh }) => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (companyId) {
      fetchTemplate();
    }
  }, [companyId]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/companies/${companyId}/template`);
      setTemplate(response.data);
    } catch (error) {
      console.error('Error fetching template:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(`/companies/${companyId}/template`, template);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleFieldVisibility = (fieldName) => {
    setTemplate(prev => ({
      ...prev,
      fields: {
        ...prev.fields,
        [fieldName]: {
          ...prev.fields[fieldName],
          visible: !prev.fields[fieldName].visible
        }
      }
    }));
  };

  const toggleFieldEditability = (fieldName) => {
    setTemplate(prev => ({
      ...prev,
      fields: {
        ...prev.fields,
        [fieldName]: {
          ...prev.fields[fieldName],
          editable: !prev.fields[fieldName].editable
        }
      }
    }));
  };

  if (!companyId) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <Palette className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Select a Company
            </h3>
            <p className="text-gray-500">
              Please select a company to manage its signature template.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-pink-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Palette className="h-5 w-5 mr-2" />
            Signature Template
          </CardTitle>
          <CardDescription>
            Customize the signature template for {selectedCompany?.name || 'your company'}. 
            Employees will use this template to generate their signatures.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Branding Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Branding</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Brand Color</Label>
                <div className="flex items-center space-x-2">
                  <Input
                    type="color"
                    value={template?.brand_color || '#EB2F5B'}
                    onChange={(e) =>
                      setTemplate(prev => ({ ...prev, brand_color: e.target.value }))
                    }
                    className="w-12 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    value={template?.brand_color || '#EB2F5B'}
                    onChange={(e) =>
                      setTemplate(prev => ({ ...prev, brand_color: e.target.value }))
                    }
                    className="flex-1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="book-meeting-text">Book Meeting Button Text</Label>
                <Input
                  id="book-meeting-text"
                  value={template?.book_meeting_text || ''}
                  onChange={(e) =>
                    setTemplate(prev => ({ ...prev, book_meeting_text: e.target.value }))
                  }
                  placeholder="Book a Meeting"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="disclaimer">Email Disclaimer</Label>
              <Textarea
                id="disclaimer"
                value={template?.disclaimer || ''}
                onChange={(e) =>
                  setTemplate(prev => ({ ...prev, disclaimer: e.target.value }))
                }
                placeholder="This email and any files transmitted with it are confidential..."
                rows={3}
              />
            </div>
          </div>

          {/* Field Configuration Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Field Configuration</h3>
            <p className="text-sm text-gray-600">
              Control which fields employees can see and edit when generating their signatures
            </p>

            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-4 font-medium text-sm text-gray-700 pb-2 border-b">
                <div className="col-span-5">Field Name</div>
                <div className="col-span-3 text-center">Visible</div>
                <div className="col-span-4 text-center">Employee Can Edit</div>
              </div>

              {template?.fields && Object.entries(template.fields).map(([fieldName, config]) => (
                <div key={fieldName} className="grid grid-cols-12 gap-4 items-center py-2 hover:bg-gray-50 rounded px-2">
                  <div className="col-span-5 capitalize">{fieldName.replace(/_/g, ' ')}</div>
                  <div className="col-span-3 flex justify-center">
                    <Switch
                      checked={config.visible}
                      onCheckedChange={() => toggleFieldVisibility(fieldName)}
                    />
                  </div>
                  <div className="col-span-4 flex justify-center">
                    <Switch
                      checked={config.editable}
                      onCheckedChange={() => toggleFieldEditability(fieldName)}
                      disabled={!config.visible}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {success && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Template saved successfully!
              </AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end space-x-4">
            <Button
              variant="outline"
              onClick={() => window.open(`/clients/${selectedCompany?.signature_url}`, '_blank')}
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              style={{ backgroundColor: '#EB2F5B' }}
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Save Template
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Generations Tab Component
const GenerationsTab = ({ companyId }) => {
  const [generations, setGenerations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (companyId) {
      fetchGenerations();
    }
  }, [companyId]);

  const fetchGenerations = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/signatures/generations?company_id=${companyId}`);
      setGenerations(response.data);
    } catch (error) {
      console.error('Error fetching generations:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!companyId) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Select a Company
            </h3>
            <p className="text-gray-500">
              Please select a company to view signature generations.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Generated Signatures</CardTitle>
        <CardDescription>
          View all signatures generated by employees
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-pink-600" />
          </div>
        ) : generations.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No signatures generated yet
            </h3>
            <p className="text-gray-500">
              Employees haven't generated any signatures yet.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {generations.map((gen) => (
              <div
                key={gen.id}
                className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900">
                      {gen.employee_data.full_name}
                    </h3>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Email:</span>{' '}
                        {gen.employee_data.email}
                      </div>
                      <div>
                        <span className="font-medium">Job Title:</span>{' '}
                        {gen.employee_data.job_title || 'Not set'}
                      </div>
                      <div>
                        <span className="font-medium">Generated:</span>{' '}
                        {formatDate(gen.generated_at)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Default Signature Tab Component
const DefaultSignatureTab = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null); // null, 'profile', 'logo', 'banner'

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/default-signature');
      setSettings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch default signature settings:', error);
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, imageType) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingImage(imageType);
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fieldMap = {
        'profile': 'default_profile_image_url',
        'logo': 'default_company_logo_url',
        'banner': 'default_banner_url'
      };

      setSettings(prev => ({
        ...prev,
        [fieldMap[imageType]]: response.data.url
      }));
      setUploadingImage(null);
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image');
      setUploadingImage(null);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/admin/default-signature', settings);
      alert('Default signature settings saved successfully!');
      setSaving(false);
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings');
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB2F5B] mx-auto"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Default Signature Settings</CardTitle>
          <CardDescription>
            Customize everything users see by default when they sign up. Control which fields are visible, what sample data is shown, and what users can edit.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-8">
          
          {/* PROFILE IMAGE */}
          <div className="space-y-4 border-b pb-6">
            <h3 className="font-semibold text-lg">Profile Image</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Default Profile Image</label>
                <div className="flex items-center gap-4">
                  {settings.default_profile_image_url && (
                    <img
                      src={settings.default_profile_image_url}
                      alt="Default profile"
                      className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                    />
                  )}
                  <div>
                    <label className="cursor-pointer">
                      <div className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm font-medium">
                        {uploadingImage === 'profile' ? 'Uploading...' : 'Upload Image'}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'profile')}
                        className="hidden"
                        disabled={uploadingImage !== null}
                      />
                    </label>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.show_profile_image}
                    onChange={(e) => updateField('show_profile_image', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Show profile image by default</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.allow_edit_profile_image}
                    onChange={(e) => updateField('allow_edit_profile_image', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Allow users to edit profile image</span>
                </label>
              </div>
            </div>
          </div>

          {/* BASIC INFO */}
          <div className="space-y-4 border-b pb-6">
            <h3 className="font-semibold text-lg">Basic Information</h3>
            
            {/* Name */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
              <div className="md:col-span-2">
                <label className="text-sm font-medium block mb-1">Default Name</label>
                <input
                  type="text"
                  value={settings.default_name}
                  onChange={(e) => updateField('default_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="John Doe"
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.show_name}
                    onChange={(e) => updateField('show_name', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Show</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.allow_edit_name}
                    onChange={(e) => updateField('allow_edit_name', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Allow Edit</span>
                </label>
              </div>
            </div>

            {/* Job Title */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
              <div className="md:col-span-2">
                <label className="text-sm font-medium block mb-1">Default Job Title</label>
                <input
                  type="text"
                  value={settings.default_job_title}
                  onChange={(e) => updateField('default_job_title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Professional"
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.show_job_title}
                    onChange={(e) => updateField('show_job_title', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Show</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.allow_edit_job_title}
                    onChange={(e) => updateField('allow_edit_job_title', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Allow Edit</span>
                </label>
              </div>
            </div>

            {/* Credentials */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
              <div className="md:col-span-2">
                <label className="text-sm font-medium block mb-1">Default Credentials</label>
                <input
                  type="text"
                  value={settings.default_credentials || ''}
                  onChange={(e) => updateField('default_credentials', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="MBA, CPA"
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.show_credentials}
                    onChange={(e) => updateField('show_credentials', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Show</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.allow_edit_credentials}
                    onChange={(e) => updateField('allow_edit_credentials', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Allow Edit</span>
                </label>
              </div>
            </div>
          </div>

          {/* CONTACT INFO */}
          <div className="space-y-4 border-b pb-6">
            <h3 className="font-semibold text-lg">Contact Information</h3>
            
            {['email', 'phone', 'mobile', 'fax', 'website', 'address'].map((field) => (
              <div key={field} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                <div className="md:col-span-2">
                  <label className="text-sm font-medium block mb-1 capitalize">Default {field.replace('_', ' ')}</label>
                  <input
                    type={field === 'email' ? 'email' : 'text'}
                    value={settings[`default_${field}`] || ''}
                    onChange={(e) => updateField(`default_${field}`, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder={field === 'email' ? 'user@company.com' : field === 'phone' ? '+1 (555) 123-4567' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings[`show_${field}`]}
                      onChange={(e) => updateField(`show_${field}`, e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm">Show</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings[`allow_edit_${field}`]}
                      onChange={(e) => updateField(`allow_edit_${field}`, e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm">Allow Edit</span>
                  </label>
                </div>
              </div>
            ))}
          </div>

          {/* COMPANY BRANDING */}
          <div className="space-y-4 border-b pb-6">
            <h3 className="font-semibold text-lg">Company Branding</h3>
            
            {/* Logo */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Company Logo</label>
                <div className="flex items-center gap-4">
                  {settings.default_company_logo_url && (
                    <img
                      src={settings.default_company_logo_url}
                      alt="Company logo"
                      className="h-16 object-contain border p-2"
                    />
                  )}
                  <div>
                    <label className="cursor-pointer">
                      <div className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm font-medium">
                        {uploadingImage === 'logo' ? 'Uploading...' : 'Upload Logo'}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'logo')}
                        className="hidden"
                        disabled={uploadingImage !== null}
                      />
                    </label>
                  </div>
                </div>
                <input
                  type="url"
                  value={settings.default_company_logo_link || ''}
                  onChange={(e) => updateField('default_company_logo_link', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="Logo click URL (optional)"
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.show_logo}
                    onChange={(e) => updateField('show_logo', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Show company logo by default</span>
                </label>
              </div>
            </div>

            {/* Banner */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Banner Image</label>
                <div className="flex items-center gap-4">
                  {settings.default_banner_url && (
                    <img
                      src={settings.default_banner_url}
                      alt="Banner"
                      className="h-16 object-contain border"
                    />
                  )}
                  <div>
                    <label className="cursor-pointer">
                      <div className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm font-medium">
                        {uploadingImage === 'banner' ? 'Uploading...' : 'Upload Banner'}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, 'banner')}
                        className="hidden"
                        disabled={uploadingImage !== null}
                      />
                    </label>
                  </div>
                </div>
                <input
                  type="url"
                  value={settings.default_banner_link || ''}
                  onChange={(e) => updateField('default_banner_link', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  placeholder="Banner click URL (optional)"
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.show_banner}
                    onChange={(e) => updateField('show_banner', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Show banner by default</span>
                </label>
              </div>
            </div>

            {/* Brand Color */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Default Brand Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={settings.default_brand_color}
                    onChange={(e) => updateField('default_brand_color', e.target.value)}
                    className="h-10 w-20"
                  />
                  <input
                    type="text"
                    value={settings.default_brand_color}
                    onChange={(e) => updateField('default_brand_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="#EB2F5B"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.show_brand_color}
                    onChange={(e) => updateField('show_brand_color', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Show brand color by default</span>
                </label>
              </div>
            </div>
          </div>

          {/* SOCIAL MEDIA */}
          <div className="space-y-4 border-b pb-6">
            <h3 className="font-semibold text-lg">Social Media Links</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div></div>
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.show_social_links}
                    onChange={(e) => updateField('show_social_links', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm font-medium">Show social media links by default</span>
                </label>
                <label className="flex items-center gap-2 mt-2">
                  <input
                    type="checkbox"
                    checked={settings.allow_edit_social_links}
                    onChange={(e) => updateField('allow_edit_social_links', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm font-medium">Allow users to edit social links</span>
                </label>
              </div>
            </div>

            {['linkedin', 'x_twitter', 'facebook', 'instagram', 'youtube'].map((social) => (
              <div key={social}>
                <label className="text-sm font-medium block mb-1 capitalize">
                  {social.replace('_', ' / ').replace('x', 'X')}
                </label>
                <input
                  type="url"
                  value={settings[`default_${social}`] || ''}
                  onChange={(e) => updateField(`default_${social}`, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder={`https://${social.replace('_', '').replace('twitter', 'x')}.com/username`}
                />
              </div>
            ))}
          </div>

          {/* OTHER FIELDS */}
          <div className="space-y-4 border-b pb-6">
            <h3 className="font-semibold text-lg">Additional Fields</h3>
            
            {/* Book Meeting */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
              <div className="md:col-span-2">
                <label className="text-sm font-medium block mb-1">Book Meeting Link</label>
                <input
                  type="url"
                  value={settings.default_book_meeting || ''}
                  onChange={(e) => updateField('default_book_meeting', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="https://calendly.com/your-link"
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.show_book_meeting}
                    onChange={(e) => updateField('show_book_meeting', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Show</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.allow_edit_book_meeting}
                    onChange={(e) => updateField('allow_edit_book_meeting', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Allow Edit</span>
                </label>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
              <div className="md:col-span-2">
                <label className="text-sm font-medium block mb-1">Disclaimer Text</label>
                <textarea
                  value={settings.default_disclaimer || ''}
                  onChange={(e) => updateField('default_disclaimer', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows="3"
                  placeholder="This email and any files transmitted with it are confidential..."
                />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={settings.show_disclaimer}
                    onChange={(e) => updateField('show_disclaimer', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Show disclaimer</span>
                </label>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="pt-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full bg-[#EB2F5B] hover:bg-[#d02850] text-lg py-6"
            >
              {saving ? 'Saving...' : '💾 Save All Default Signature Settings'}
            </Button>
          </div>

          <Alert>
            <AlertDescription className="text-sm">
              <strong>How it works:</strong> These settings control what new users see when they sign up and create their signature. Users can override any editable fields with their own information. Fields marked as "not editable" will be locked for all users.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPortal;
